import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { ReactNode, useState } from 'react';

export type ListItem = {
  key: number | string;
  value: ReactNode;
  type?: string;
};

type Props = {
  list: ListItem[];
  checked?: ListItem[];
  setChecked?: (items: ListItem[]) => void;
  maxHeight?: React.ReactText;
  isSelectAllEnabled?: boolean;
};

const StyledUl = styled('ul')(({ theme }) => ({
  padding: 0,
  margin: 0,
  listStyleType: 'none',
  border: `1px solid ${theme.palette.grey[400]}`,
  maxHeight: 'calc(100vh - 300px)',
  overflow: 'auto',

  '&:hover': {
    cursor: 'pointer',
  },

  li: {
    width: 400,
    minHeight: 30,
    background: theme.palette.grey[50],

    ':nth-child(odd)': {
      background: theme.palette.grey[100],
    },

    ':hover': {
      background: '#E6F6FC',
    },

    '&.checked': {
      background: '#C4DAEB',

      ':hover': {
        background: '#C4DAEB',
      },

      p: {
        fontWeight: 700,
      },
    },
  },

  p: {
    fontSize: '0.875rem',
    padding: theme.spacing(0.5, 5),
  },

  '.empty': {
    color: theme.palette.grey[600],
    textAlign: 'center',
    fontStyle: 'italic',

    ':hover': {
      background: theme.palette.grey[100],
    },
  },

  '.deselect': {
    fontStyle: 'italic',
  },
}));

const ListMultiSelect: React.FC<Props> = ({
  list,
  checked,
  setChecked,
  maxHeight,
  isSelectAllEnabled,
}) => {
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectAll, setSelectAll] = useState({
    label: `[A] SELECT ALL`,
  });

  const toggleSelectAll = () => {
    if (checked) {
      if (isSelectAll) {
        const newChecked = [...checked];
        newChecked.length = 0;
        setChecked?.(newChecked);
        setSelectAll({ ...selectAll, label: `[A] SELECT ALL` });
        setIsSelectAll(false);
      } else {
        const newChecked = [...checked];
        newChecked.length = 0;
        list.forEach((item) => {
          newChecked.push(item);
        });
        setChecked?.(newChecked);
        setSelectAll({ ...selectAll, label: `[A] DESELECT` });
        setIsSelectAll(true);
      }
    }
  };

  const toggleChecked = (item: ListItem) => {
    if (checked) {
      const { key } = item;
      const currentIndex = checked.findIndex((item) => item.key === key);
      const newChecked = [...checked];

      if (currentIndex === -1) {
        newChecked.push(item);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setChecked?.(newChecked);

      if (newChecked.length === list.length) {
        setSelectAll({ ...selectAll, label: `[A] DESELECT` });
        setIsSelectAll(true);
      } else {
        setSelectAll({ ...selectAll, label: `[A] SELECT ALL` });
        setIsSelectAll(false);
      }
    }
  };

  return (
    <StyledUl className="listMultiSelect" sx={{ maxHeight }}>
      {list.length && isSelectAllEnabled == undefined ? (
        <li
          className={isSelectAll ? 'checked deselect' : ''}
          onClick={() => toggleSelectAll()}
        >
          <Typography>{selectAll.label}</Typography>
        </li>
      ) : null}

      {list.map(({ key, value, type }) => (
        <li
          key={key}
          className={checked?.some((item) => item.key === key) ? 'checked' : ''}
          onClick={() => toggleChecked({ key, value, type })}
        >
          <Typography>{value}</Typography>
        </li>
      ))}
      {!list.length && <li className="empty">empty</li>}
    </StyledUl>
  );
};

export default ListMultiSelect;
